import React, { useEffect, useState } from "react";
import { Loader2, Eye, Check, X, Filter, PencilLine } from "lucide-react";
import api, { apiError, fmtTime, fmtDateLong } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";

const STATUSES = ["BELUM CHECK-IN", "SEDANG BEKERJA", "SUDAH CHECK-OUT", "PERLU REVIEW", "PULANG LEBIH AWAL", "LUPA CHECK-OUT", "UNRESOLVED"];

const Field = ({ label, value }) => (
  <div className="flex justify-between py-1.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-700 text-right">{value ?? "-"}</span>
  </div>
);

export default function RiwayatTab() {
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [correction, setCorrection] = useState(null);
  const [correctionTime, setCorrectionTime] = useState("");
  const [correctionNote, setCorrectionNote] = useState("");
  const [f, setF] = useState({
    date_from: "",
    date_to: "",
    user_id: "all",
    outlet_id: "all",
    status: "all",
  });

  const loadMasters = async () => {
    const [u, o] = await Promise.all([api.get("/users"), api.get("/outlets")]);
    setUsers(u.data); setOutlets(o.data);
  };
  const load = async () => {
    const p = new URLSearchParams();
    if (f.date_from) p.append("date_from", f.date_from);
    if (f.date_to) p.append("date_to", f.date_to);
    if (f.user_id !== "all") p.append("user_id", f.user_id);
    if (f.outlet_id !== "all") p.append("outlet_id", f.outlet_id);
    if (f.status !== "all") p.append("status", f.status);
    const { data } = await api.get(`/attendance?${p.toString()}`);
    setRecords(data);
  };

  useEffect(() => { loadMasters(); }, []);
  useEffect(() => { load().finally(() => setLoading(false)); }, [f]);

  const act = async (id, action) => {
    try {
      await api.post(`/attendance/${id}/${action}`);
      toast.success(action === "approve" ? "Absensi disetujui" : "Absensi ditolak");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitCorrection = async () => {
    if (!correctionTime) return toast.error("Isi waktu check-out aktual");
    try {
      await api.post(`/attendance/${correction.id}/checkout-correction`, {
        check_out_time: correctionTime,
        note: correctionNote,
      });
      toast.success("Check-out berhasil dikoreksi");
      setCorrection(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-bold text-slate-800">Riwayat Absensi & Approval</h2>

      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-3"><Filter className="h-4 w-4" /> Filter</div>
        <div className="grid sm:grid-cols-5 gap-3">
          <div className="space-y-1.5"><Label>Tanggal Mulai</Label>
            <Input type="date" data-testid="filter-date-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Tanggal Selesai</Label>
            <Input type="date" min={f.date_from || undefined} data-testid="filter-date-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Karyawan</Label>
            <Select value={f.user_id} onValueChange={(v) => setF({ ...f, user_id: v })}>
              <SelectTrigger data-testid="filter-user"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Outlet</Label>
            <Select value={f.outlet_id} onValueChange={(v) => setF({ ...f, outlet_id: v })}>
              <SelectTrigger data-testid="filter-outlet"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua</SelectItem>{outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#3368A0]" /></div> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="riwayat-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-3">Tanggal</th>
                <th className="text-left px-5 py-3">Karyawan</th>
                <th className="text-left px-5 py-3">Check-In</th>
                <th className="text-left px-5 py-3">Check-Out</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Approval</th>
                <th className="text-right px-5 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">Tidak ada data absensi.</td></tr>}
              {records.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-5 py-3 text-slate-600">{fmtDateLong(r.schedule_date)}</td>
                  <td className="px-5 py-3 font-medium text-slate-700">{r.user_name}</td>
                  <td className="px-5 py-3 font-mono text-slate-600">{fmtTime(r.check_in_time)}</td>
                  <td className="px-5 py-3 font-mono text-slate-600">{fmtTime(r.check_out_time)}</td>
                  <td className="px-5 py-3"><StatusBadge status={r.attendance_status} /></td>
                  <td className="px-5 py-3">{r.attendance_status === "PERLU REVIEW" ? <StatusBadge status={r.approval_status} /> : <span className="text-slate-300">-</span>}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setDetail(r)} data-testid={`detail-${r.id}`} className="text-[#3368A0] hover:text-sky-800 p-1.5"><Eye className="h-4 w-4" /></button>
                    {r.attendance_status === "LUPA CHECK-OUT" && (
                      <button onClick={() => { setCorrection(r); setCorrectionTime(""); setCorrectionNote(""); }} data-testid={`checkout-correction-${r.id}`} className="text-amber-600 hover:text-amber-800 p-1.5"><PencilLine className="h-4 w-4" /></button>
                    )}
                    {(["PERLU REVIEW", "PULANG LEBIH AWAL"].includes(r.attendance_status)) && r.approval_status === "PENDING" && (
                      <>
                        <button onClick={() => act(r.id, "approve")} data-testid="approve-attendance-button" className="text-emerald-600 hover:text-emerald-800 p-1.5"><Check className="h-4 w-4" /></button>
                        <button onClick={() => act(r.id, "reject")} data-testid="reject-attendance-button" className="text-rose-500 hover:text-rose-700 p-1.5"><X className="h-4 w-4" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="attendance-detail-modal">
          <DialogHeader><DialogTitle>Detail Absensi — {detail?.user_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">{fmtDateLong(detail.schedule_date)} · {detail.shift_name} ({detail.shift_time}) · {detail.outlet_name}</p>
              <div>
                <p className="font-semibold text-[#284f7a] mb-1">Check-In</p>
                <Field label="Waktu" value={fmtTime(detail.check_in_time)} />
                <Field label="Latitude" value={detail.check_in_latitude?.toFixed?.(6)} />
                <Field label="Longitude" value={detail.check_in_longitude?.toFixed?.(6)} />
                <Field label="GPS Accuracy" value={detail.check_in_accuracy != null ? `${Math.round(detail.check_in_accuracy)} m` : "-"} />
                <Field label="Distance to Outlet" value={detail.check_in_distance != null ? `${detail.check_in_distance} m` : "-"} />
                <Field label="Location Status" value={<StatusBadge status={detail.check_in_location_status} />} />
                <Field label="Punctuality" value={<StatusBadge status={detail.punctuality_status} />} />
                <Field label="Actual Late Minutes" value={`${detail.actual_late_minutes ?? 0} menit`} />
              </div>
              <div>
                <p className="font-semibold text-[#3f7f9c] mb-1">Check-Out</p>
                <Field label="Waktu" value={fmtTime(detail.check_out_time)} />
                <Field label="Latitude" value={detail.check_out_latitude?.toFixed?.(6)} />
                <Field label="Longitude" value={detail.check_out_longitude?.toFixed?.(6)} />
                <Field label="GPS Accuracy" value={detail.check_out_accuracy != null ? `${Math.round(detail.check_out_accuracy)} m` : "-"} />
                <Field label="Distance to Outlet" value={detail.check_out_distance != null ? `${detail.check_out_distance} m` : "-"} />
                <Field label="Location Status" value={detail.check_out_location_status ? <StatusBadge status={detail.check_out_location_status} /> : "-"} />
                <Field label="Check-Out Status" value={detail.check_out_status ? <StatusBadge status={detail.check_out_status} /> : "-"} />
                <Field label="Early Leave Minutes" value={detail.early_leave_minutes != null ? `${detail.early_leave_minutes} menit` : "-"} />
                <Field label="Jenis Exception" value={detail.exception_type || "-"} />
              </div>
              {(["PERLU REVIEW", "PULANG LEBIH AWAL"].includes(detail.attendance_status)) && (
                <div className="bg-purple-50 rounded-xl p-3">
                  <Field label="Approval Status" value={<StatusBadge status={detail.approval_status} />} />
                  {detail.approval_status === "PENDING" && (
                    <div className="flex gap-2 mt-3">
                      <Button onClick={() => { act(detail.id, "approve"); setDetail(null); }} className="flex-1 bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4 mr-1.5" /> Approve</Button>
                      <Button onClick={() => { act(detail.id, "reject"); setDetail(null); }} variant="destructive" className="flex-1"><X className="h-4 w-4 mr-1.5" /> Reject</Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!correction} onOpenChange={(v) => !v && setCorrection(null)}>
        <DialogContent className="bg-white" data-testid="checkout-correction-modal">
          <DialogHeader><DialogTitle>Check-out Correction</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500">{correction?.user_name} · {correction?.shift_time}</p>
            <div className="space-y-1.5"><Label>Waktu Check-Out Aktual</Label>
              <Input type="datetime-local" data-testid="correction-time-input" value={correctionTime} onChange={(e) => setCorrectionTime(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Catatan</Label>
              <Input data-testid="correction-note-input" value={correctionNote} onChange={(e) => setCorrectionNote(e.target.value)} placeholder="Alasan koreksi" /></div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setCorrection(null)}>Batal</Button><Button data-testid="save-checkout-correction-button" onClick={submitCorrection} className="bg-[#3368A0] hover:bg-[#284f7a]">Simpan Koreksi</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
